import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Sparkles, Heart, Users, Megaphone, Flag, PartyPopper, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const categoryConfig = {
  anniversary: { icon: PartyPopper, color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', label: { en: 'Anniversary', bn: 'বার্ষিকী' } },
  welfare: { icon: Heart, color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500', label: { en: 'Welfare', bn: 'কল্যাণ' } },
  cultural: { icon: Sparkles, color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500', label: { en: 'Cultural', bn: 'সাংস্কৃতিক' } },
  rally: { icon: Megaphone, color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', label: { en: 'Rally', bn: 'সমাবেশ' } },
  political: { icon: Flag, color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500', label: { en: 'Political', bn: 'রাজনৈতিক' } },
  social: { icon: Users, color: 'bg-teal-100 text-teal-700 border-teal-200', dot: 'bg-teal-500', label: { en: 'Social', bn: 'সামাজিক' } },
};

const Events = () => {
  const { language, t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 8)); // Sep 2026
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch('/api/events/');
        if (response.ok) setEvents(await response.json());
      } catch (error) { console.error('Error:', error); }
      finally { setLoading(false); }
    };
    fetchEvents();
  }, []);

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const monthNames = language === 'en'
    ? ['January','February','March','April','May','June','July','August','September','October','November','December']
    : ['জানুয়ারি','ফেব্রুয়ারি','মার্চ','এপ্রিল','মে','জুন','জুলাই','আগস্ট','সেপ্টেম্বর','অক্টোবর','নভেম্বর','ডিসেম্বর'];

  const dayNames = language === 'en' ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] : ['রবি','সোম','মঙ্গল','বুধ','বৃহঃ','শুক্র','শনি'];

  const getEventsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const filteredEvents = activeFilter === 'all' ? events : events.filter(e => e.category === activeFilter);

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="events-page">
      {/* Hero */}
      <section className="bg-party-red py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            {t({ en: '37th Anniversary Programme', bn: '৩৭তম বার্ষিকী কর্মসূচি' })}
          </h1>
          <p className="text-lg text-red-100 max-w-3xl mx-auto">
            {t({ en: 'September — December 2026: Three months of events celebrating NDP\'s founding anniversary', bn: 'সেপ্টেম্বর — ডিসেম্বর ২০২৬: এনডিপির প্রতিষ্ঠাবার্ষিকী উদযাপনে তিন মাসব্যাপী কর্মসূচি' })}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden" data-testid="event-calendar">
              <div className="bg-gray-900 text-white p-4 flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={prevMonth} className="text-white hover:bg-white/10" data-testid="calendar-prev">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <h2 className="text-xl font-bold">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <Button variant="ghost" size="sm" onClick={nextMonth} className="text-white hover:bg-white/10" data-testid="calendar-next">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <CardContent className="p-4">
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map(d => (
                    <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>
                  ))}
                </div>
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {[...Array(firstDay)].map((_, i) => <div key={`e-${i}`} />)}
                  {[...Array(daysInMonth)].map((_, i) => {
                    const day = i + 1;
                    const dayEvents = getEventsForDate(day);
                    const hasEvents = dayEvents.length > 0;
                    const isSelected = selectedDate === day;
                    const isToday = day === new Date().getDate() && currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear();
                    return (
                      <div
                        key={day}
                        onClick={() => hasEvents && setSelectedDate(isSelected ? null : day)}
                        className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-all
                          ${hasEvents ? 'cursor-pointer hover:bg-party-red/10 font-bold' : 'text-gray-400'}
                          ${isSelected ? 'bg-party-red text-white ring-2 ring-party-red' : ''}
                          ${isToday && !isSelected ? 'ring-2 ring-party-green' : ''}
                        `}
                        data-testid={hasEvents ? `calendar-day-${day}` : undefined}
                      >
                        <span>{day}</span>
                        {hasEvents && (
                          <div className="flex gap-0.5 mt-0.5">
                            {dayEvents.slice(0, 3).map((ev, idx) => {
                              const cat = categoryConfig[ev.category] || categoryConfig.political;
                              return <div key={idx} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : cat.dot}`} />;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selected date events */}
                {selectedDate && (
                  <div className="mt-4 pt-4 border-t space-y-3" data-testid="selected-date-events">
                    {getEventsForDate(selectedDate).map((event, idx) => {
                      const cat = categoryConfig[event.category] || categoryConfig.political;
                      const Icon = cat.icon;
                      return (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`w-8 h-8 rounded-full ${cat.color} flex items-center justify-center shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm">{t(event.title)}</h4>
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3" /> {t(event.location)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Event Legend + Filters */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-4">{t({ en: 'Event Categories', bn: 'ইভেন্ট বিভাগ' })}</h3>
                <div className="space-y-2">
                  <button onClick={() => setActiveFilter('all')} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeFilter === 'all' ? 'bg-gray-900 text-white' : 'hover:bg-gray-100'}`} data-testid="filter-all">
                    {t({ en: 'All Events', bn: 'সকল ইভেন্ট' })} ({events.length})
                  </button>
                  {Object.entries(categoryConfig).map(([key, val]) => {
                    const count = events.filter(e => e.category === key).length;
                    if (count === 0) return null;
                    const Icon = val.icon;
                    return (
                      <button key={key} onClick={() => setActiveFilter(key)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeFilter === key ? 'bg-gray-900 text-white' : 'hover:bg-gray-100'}`} data-testid={`filter-${key}`}>
                        <Icon className="h-4 w-4" />
                        <span>{t(val.label)}</span>
                        <span className="ml-auto text-xs opacity-60">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-2">{t({ en: 'Programme Duration', bn: 'কর্মসূচির সময়কাল' })}</h3>
                <p className="text-sm text-gray-500 mb-3">{t({ en: 'Sep 11 — Dec 11, 2026', bn: '১১ সেপ্টেম্বর — ১১ ডিসেম্বর ২০২৬' })}</p>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-party-red via-party-green to-party-red rounded-full" style={{ width: '100%' }} />
                </div>
                <p className="text-xs text-gray-400 mt-2">{t({ en: '12 events across 3 months', bn: '৩ মাসে ১২টি অনুষ্ঠান' })}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Timeline List */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {t({ en: 'Full Programme Schedule', bn: 'সম্পূর্ণ কর্মসূচি তালিকা' })}
          </h2>
          <div className="space-y-4">
            {filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date)).map((event) => {
              const cat = categoryConfig[event.category] || categoryConfig.political;
              const Icon = cat.icon;
              const eventDate = new Date(event.date);
              const isPast = event.status === 'completed';
              return (
                <Card key={event._id} className={`overflow-hidden transition-all hover:shadow-lg ${isPast ? 'opacity-75' : ''}`} data-testid={`event-card-${event._id}`}>
                  <div className="flex">
                    {/* Date column */}
                    <div className={`w-24 shrink-0 flex flex-col items-center justify-center p-3 ${isPast ? 'bg-gray-200' : 'bg-party-red'} text-white`}>
                      <span className={`text-3xl font-bold ${isPast ? 'text-gray-600' : ''}`}>{eventDate.getDate()}</span>
                      <span className={`text-xs uppercase ${isPast ? 'text-gray-500' : 'text-red-100'}`}>
                        {eventDate.toLocaleDateString(language === 'en' ? 'en-US' : 'bn-BD', { month: 'short' })}
                      </span>
                    </div>
                    {/* Content */}
                    <CardContent className="p-4 flex-1">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full ${cat.color} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-bold text-gray-900">{t(event.title)}</h3>
                            {isPast && <Badge variant="outline" className="text-xs">{t({ en: 'Completed', bn: 'সম্পন্ন' })}</Badge>}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{t(event.description)}</p>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {t(event.location)}
                            </p>
                            <EventShareButtons title={t(event.title)} date={event.date} />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;

const EventShareButtons = ({ title, date }) => {
  const pageUrl = typeof window !== 'undefined' ? window.location.origin + '/events' : '';
  const text = `${title} — ${date}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(text)}`;
  const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + pageUrl)}`;
  return (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="event-share-buttons">
      <a href={fbUrl} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2] text-[#1877F2] hover:text-white flex items-center justify-center transition-colors" data-testid="event-share-fb" onClick={e => e.stopPropagation()}>
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </a>
      <a href={waUrl} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-[#25D366]/10 hover:bg-[#25D366] text-[#25D366] hover:text-white flex items-center justify-center transition-colors" data-testid="event-share-wa" onClick={e => e.stopPropagation()}>
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
    </div>
  );
};
